import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { format, addDays, subDays, parseISO } from "date-fns";
import "./CashEntriesPage.css";
import { MdClose } from "react-icons/md";
import { TbArrowBigLeftFilled, TbArrowBigRightFilled } from "react-icons/tb";


const SubwayCashInPage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [storeNumber, setStoreNumber] = useState("");
  const [categories, setCategories] = useState([]);
  const [entryData, setEntryData] = useState({});
  const [tempEntryData, setTempEntryData] = useState({});
  const [salesRows, setSalesRows] = useState([]);
  const [tenderRows, setTenderRows] = useState([]);
  const [paidoutRows, setPaidoutRows] = useState([]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDateInput, setStartDateInput] = useState(format(new Date(), "yyyy-MM-dd"));

  const [showAddCat, setShowAddCat] = useState(false);
  const [showEditCat, setShowEditCat] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", category_type: "" });
  const [editCat, setEditCat] = useState({ id: "", name: "", category_type: "" });

  const [loadingStore, setLoadingStore] = useState(true);
  

  const inputRefs = useRef({});
  

  const CATEGORY_TYPES = [
    { value: "income", label: "income" },
    { value: "non-sale", label: "non-sale income" },
    { value: "payment", label: "payment" },
    { value: "paidouts", label: "paidouts" },
    { value: "refunds", label: "refunds" },
  ];

  const RESERVED = new Set(["Open Cash", "Close Cash", "Required Cash", "Cash Advance"]);

  const daysToShow = [-3, -2, -1, 0, 1, 2, 3].map((offset) =>
    format(addDays(currentDate, offset), "yyyy-MM-dd")
  );

  

  const loadBusinessStore = useCallback(async () => {
    try {
      setLoadingStore(true);
      const { data } = await api.get(`/subway-data/business-store/${businessId}`);
      setStoreNumber(String(data?.store_number || "").trim());
    } catch (err) {
      console.error("Error loading business store number:", err);
      alert(err?.response?.data?.message || "Failed to load business store number");
      setStoreNumber("");
    } finally {
      setLoadingStore(false);
    }
  }, [businessId]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get(`/cash-register-categories`, {
        params: { business_id: businessId },
      });
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
      alert("Failed to load categories");
    }
  }, [businessId]);

  const loadEntries = useCallback(async () => {
    try {
      const startDate = format(addDays(currentDate, -3), "yyyy-MM-dd");
      const endDate = format(addDays(currentDate, 3), "yyyy-MM-dd");

      const { data } = await api.get("/cash-register-entries", {
        params: { business_id: businessId, start_date: startDate, end_date: endDate },
      });

      const newEntryData = {};

      data.forEach((entry) => {
        const dateKey = format(new Date(entry.entry_date), "yyyy-MM-dd");
        const key = `${entry.category_id}_${dateKey}`;

        if (entry.category_name !== "Cash Advance") {
          newEntryData[key] = { id: entry.id, amount: entry.amount };
        }
      });

      setEntryData(newEntryData);
    } catch (err) {
      console.error("Error loading entries:", err);
      alert("Failed to load saved entries");
    }
  }, [businessId, currentDate]);

  const loadSalesRows = useCallback(async () => {
    if (!storeNumber) {
      setSalesRows([]);
      return;
    }

    try {
      const { data } = await api.get(`/subway-data/sales/${storeNumber}`);
      setSalesRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading subway sales:", err);
      alert("Failed to load subway sales");
      setSalesRows([]);
    }
  }, [storeNumber]);

  const loadTenderRows = useCallback(async () => {
    if (!storeNumber) {
      setTenderRows([]);
      return;
    }

    try {
      const { data } = await api.get(`/subway-data/tenders/${storeNumber}`);
      setTenderRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading subway tenders:", err);
      alert("Failed to load subway tenders");
      setTenderRows([]);
    }
  }, [storeNumber]);

  const loadPaidoutRows = useCallback(async () => {
    if (!storeNumber) {
      setPaidoutRows([]);
      return;
    }

    try {
      const { data } = await api.get(`/subway-data/paidouts/${storeNumber}`);
      setPaidoutRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading subway paidouts:", err);
      alert("Failed to load subway paidouts");
      setPaidoutRows([]);
    }
  }, [storeNumber]);

  useEffect(() => {
    setEntryData({});
    setTempEntryData({});
  }, [businessId]);

  useEffect(() => {
    loadBusinessStore();
    loadCategories();
  }, [loadBusinessStore, loadCategories]);

  useEffect(() => {
    if (!categories.length) return;
    loadEntries();
  }, [categories, loadEntries]);

  useEffect(() => {
    if (!storeNumber) return;
    loadSalesRows();
    loadTenderRows();
    loadPaidoutRows();
  }, [storeNumber, loadSalesRows, loadTenderRows, loadPaidoutRows]);

  const salesByDate = useMemo(() => {
    const map = {};
    salesRows.forEach((row) => {
      const dateKey = String(row.sales_date || "").slice(0, 10);
      if (!dateKey) return;
      map[dateKey] = {
        netSales: Number(row.net_sales || 0),
        salesTax: Number(row.sales_tax || 0),
      };
    });
    return map;
  }, [salesRows]);

  const tenderTotalsByDate = useMemo(() => {
    const map = {};

    tenderRows.forEach((row) => {
      const dateKey = String(row.transaction_date || "").slice(0, 10);
      if (!dateKey) return;

      if (!map[dateKey]) {
        map[dateKey] = {
          cashTender: 0,
          totalChange: 0,
          byType: {},
        };
      }

      const tenderType = String(row.tender_type || "").trim();
      const amount = Number(row.amount || 0);
      const changeAmount = Number(row.change_amount || 0);

      if (!map[dateKey].byType[tenderType]) {
        map[dateKey].byType[tenderType] = 0;
      }

      map[dateKey].byType[tenderType] += amount;

      if (tenderType.toLowerCase().includes("cash")) {
        map[dateKey].cashTender += amount;
        map[dateKey].totalChange += changeAmount;
      }
    });

    return map;
  }, [tenderRows]);

  const paidoutTotalsByDate = useMemo(() => {
    const map = {};

    paidoutRows.forEach((row) => {
      const dateKey = String(row.paidout_date || "").slice(0, 10);
      if (!dateKey) return;

      if (!map[dateKey]) {
        map[dateKey] = {
          totalPaidouts: 0,
          rows: [],
        };
      }

      const amount = Number(row.amount || 0);
      map[dateKey].totalPaidouts += amount;
      map[dateKey].rows.push(row);
    });

    return map;
  }, [paidoutRows]);

  const rowIds = useMemo(() => {
    return categories
      .filter(
        (c) =>
          c.category_type === "paidouts" &&
          c.name !== "Cash Advance" &&
          c.name !== "Required Cash"
      )
      .map((c) => c.id);
  }, [categories]);

  const handleKeyDown = useCallback(
    (e, catId, date) => {
      const key = e.key;
      const goDown = key === "Enter" || (key === "Tab" && !e.shiftKey);
      const goUp = key === "Tab" && e.shiftKey;
      if (!goDown && !goUp) return;

      e.preventDefault();

      if (!rowIds.length) return;
      const curIndex = rowIds.indexOf(catId);
      if (curIndex === -1) return;

      const isCellEmpty = (nextCatId, nextDate) => {
        const k = `${nextCatId}_${nextDate}`;
        return !entryData[k] && tempEntryData[k] === undefined && !!inputRefs.current[k];
      };

      let idx = curIndex;
      let steps = 0;

      while (steps < rowIds.length) {
        idx = goDown ? (idx + 1) % rowIds.length : (idx - 1 + rowIds.length) % rowIds.length;
        const nextCatId = rowIds[idx];
        if (isCellEmpty(nextCatId, date)) {
          const nextEl = inputRefs.current[`${nextCatId}_${date}`];
          if (nextEl) {
            nextEl.focus();
            if (typeof nextEl.select === "function") nextEl.select();
          }
          return;
        }
        steps += 1;
      }
    },
    [rowIds, entryData, tempEntryData]
  );

  const handleInputChange = (categoryId, date, value) => {
    const key = `${categoryId}_${date}`;
    setTempEntryData((prev) => ({ ...prev, [key]: value }));
  };

  const nameExists = (name, exceptId = null) =>
    categories.some(
      (c) => c.name.toLowerCase() === name.trim().toLowerCase() && c.id !== exceptId
    );

  const cannotTouch = (cat) => !cat || cat.category_type === "till" || RESERVED.has(cat.name);

  const editableCategories = categories.filter((c) => !cannotTouch(c));

  const resetAddCat = () => setNewCat({ name: "", category_type: "" });
  const resetEditCat = () => setEditCat({ id: "", name: "", category_type: "" });

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCat.name.trim() || !newCat.category_type) {
      alert("Enter name and select type.");
      return;
    }
    if (RESERVED.has(newCat.name.trim())) {
      alert("That name is reserved.");
      return;
    }
    if (nameExists(newCat.name)) {
      alert("Category name already exists.");
      return;
    }
    try {
      await api.post("/cash-register-categories", {
        name: newCat.name.trim(),
        category_type: newCat.category_type,
        business_id: businessId,
      });
      await loadCategories();
      resetAddCat();
      setShowAddCat(false);
    } catch (err) {
      console.error("Add category error:", err);
      alert("Failed to add category");
    }
  };

  const handleEditCatSelect = (idStr) => {
    const id = parseInt(idStr, 10);
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    if (cannotTouch(cat)) {
      alert("This category can’t be edited.");
      return;
    }
    setEditCat({ id: cat.id, name: cat.name, category_type: cat.category_type });
  };

  const handleSaveEditedCategory = async (e) => {
    e.preventDefault();
    if (!editCat.id || !editCat.name.trim() || !editCat.category_type) {
      alert("Select a category and fill all fields.");
      return;
    }
    const original = categories.find((c) => c.id === editCat.id);
    if (cannotTouch(original)) {
      alert("This category can’t be edited.");
      return;
    }
    if (nameExists(editCat.name, editCat.id)) {
      alert("Another category with this name already exists.");
      return;
    }
    try {
      await api.put(`/cash-register-categories/${editCat.id}`, {
        name: editCat.name.trim(),
        category_type: editCat.category_type,
      });
      await loadCategories();
      resetEditCat();
      setShowEditCat(false);
    } catch (err) {
      console.error("Update category error:", err);
      alert("Failed to update category");
    }
  };

  const handleDisableCategory = async () => {
    if (!editCat.id) {
      alert("Select a category first.");
      return;
    }
    const cat = categories.find((c) => c.id === editCat.id);
    if (cannotTouch(cat)) {
      alert("This category can’t be disabled.");
      return;
    }
    if (!window.confirm(`Disable "${cat.name}"?`)) return;

    try {
      await api.put(`/cash-register-categories/${editCat.id}/disable`);
      await loadCategories();
      resetEditCat();
      setShowEditCat(false);
    } catch (err) {
      console.error("Disable category error:", err);
      alert("Failed to disable category");
    }
  };

  const handleJumpToDate = useCallback(() => {
    if (!startDateInput) return;
    const dt = parseISO(startDateInput);
    if (isNaN(dt.getTime())) return;
    setCurrentDate(dt);
    setShowDatePicker(false);
  }, [startDateInput]);

  

  const handleSaveForDate = async (date) => {
    const entriesToUpdate = [];
    const entriesToInsert = [];

    Object.entries(tempEntryData).forEach(([key, value]) => {
      const [category_id, entryDate] = key.split("_");
      if (entryDate !== date) return;
      if (value === "") return;

      const parsedAmount = parseFloat(value);
      if (isNaN(parsedAmount)) return;

      const savedEntry = entryData[key];
      if (savedEntry?.id) {
        entriesToUpdate.push({
          id: savedEntry.id,
          amount: parsedAmount,
          notes: null,
        });
      } else {
        entriesToInsert.push({
          category_id: parseInt(category_id, 10),
          date,
          amount: parsedAmount,
          notes: null,
        });
      }
    });

    if (!entriesToUpdate.length && !entriesToInsert.length) {
      alert(`No entries to save for ${date}`);
      return;
    }

    try {
      if (entriesToInsert.length) {
        await api.post("/cash-register-entries", {
          business_id: businessId,
          entries: entriesToInsert,
        });
      }

      if (entriesToUpdate.length) {
        await api.put("/cash-register-entries", {
          business_id: businessId,
          entries: entriesToUpdate,
        });
      }

      setTempEntryData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          const [, entryDate] = key.split("_");
          if (entryDate === date) delete updated[key];
        });
        return updated;
      });

      await loadEntries();
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save entries for this date");
    }
  };

  const paidoutCategories = useMemo(() => {
    return categories.filter(
      (c) =>
        c.category_type === "paidouts" &&
        c.name !== "Cash Advance" &&
        c.name !== "Required Cash"
    );
  }, [categories]);

  const subwayValuesByDate = useMemo(() => {
    const values = {};

    daysToShow.forEach((date) => {
      const sales = salesByDate[date] || { netSales: 0, salesTax: 0 };
      const tenders = tenderTotalsByDate[date] || { cashTender: 0, totalChange: 0 };
      const subwayPaidouts = paidoutTotalsByDate[date]?.totalPaidouts || 0;

      values[date] = {
        netSales: sales.netSales,
        salesTax: sales.salesTax,
        cashTender: tenders.cashTender,
        totalChange: tenders.totalChange,
        subwayPaidouts,
        requiredCash:
          tenders.cashTender - subwayPaidouts,
      };
    });

    return values;
  }, [daysToShow, salesByDate, tenderTotalsByDate, paidoutTotalsByDate]);

  const goLeft = () => setCurrentDate((prev) => subDays(prev, 1));
  const goRight = () => setCurrentDate((prev) => addDays(prev, 1));

  if (loadingStore) {
    return (
      <div className="cash-entries-page">
        <h1 className="pw-page-title">Loading Subway Cash In...</h1>
      </div>
    );
  }

  return (
    <div className="cash-entries-page">
      <h1 className="pw-page-title">Subway Cash In</h1>

      <div className="cash-in-cat">
        
        <button
          onClick={() => {
            setStartDateInput(format(currentDate, "yyyy-MM-dd"));
            setShowDatePicker((s) => !s);
          }}
          className="cash-jump"
          aria-label="Jump to date"
        >
          🗓️ Jump to date
        </button>
        <button
        type="button"
        className="avg-cogs-btn"
        onClick={() => navigate(`/dashboard/business/${businessId}/integration`)}
      >
        Integrations
      </button>


        <div className="cash-nav-center">
          {showDatePicker && (
            <span className="cash-jump-controls">
              <input
                type="date"
                value={startDateInput}
                onChange={(e) => setStartDateInput(e.target.value)}
              />
              <button onClick={handleJumpToDate}>Go</button>
              <button
                onClick={() => setShowDatePicker(false)}
                style={{ background: "none", color: "red" }}
              >
                <MdClose />
              </button>
            </span>
          )}
        </div>
      </div>

      <div className="cash-nav">
        <button className="left-nav-arrow" aria-label="Previous day" onClick={goLeft}>
          <TbArrowBigLeftFilled />
        </button>
        <button className="right-nav-arrow" aria-label="Next day" onClick={goRight}>
          <TbArrowBigRightFilled />
        </button>
      </div>

      <div className="entries-grid" style={{ overflowX: "auto" }}>
        <table
          className="cash-entry-table"
          border="1"
          cellPadding="8"
          style={{ borderCollapse: "collapse", minWidth: "100%" }}
        >
          <thead>
            <tr>
              <th>Category</th>
              {daysToShow.map((date) => (
                <th key={date}>{format(parseISO(date), "MMM d")}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Store Number</strong></td>
              {daysToShow.map((date) => (
                <td key={`store_${date}`}>{storeNumber}</td>
              ))}
            </tr>

            <tr>
              <td><strong>Net Sales</strong></td>
              {daysToShow.map((date) => (
                <td key={`net_sales_${date}`}>${Number(subwayValuesByDate[date]?.netSales || 0).toFixed(2)}</td>
              ))}
            </tr>

            <tr>
              <td><strong>Sales Tax</strong></td>
              {daysToShow.map((date) => (
                <td key={`sales_tax_${date}`}>${Number(subwayValuesByDate[date]?.salesTax || 0).toFixed(2)}</td>
              ))}
            </tr>

            <tr>
              <td><strong>Cash Tender</strong></td>
              {daysToShow.map((date) => (
                <td key={`cash_tender_${date}`}>${Number(subwayValuesByDate[date]?.cashTender || 0).toFixed(2)}</td>
              ))}
            </tr>

            

            <tr>
              <td><strong>Subway Paidouts</strong></td>
              {daysToShow.map((date) => (
                <td key={`subway_paidouts_${date}`}>${Number(subwayValuesByDate[date]?.subwayPaidouts || 0).toFixed(2)}</td>
              ))}
            </tr>

            {paidoutCategories.map((cat) => (
              <tr key={cat.id}>
                <td>{cat.name}</td>
                {daysToShow.map((date) => {
                  const savedEntry = entryData[`${cat.id}_${date}`];
                  const tempKey = `${cat.id}_${date}`;

                  return (
                    <td key={`${cat.id}_${date}`}>
                      {tempEntryData[tempKey] !== undefined ? (
                        <input
                          ref={(el) => {
                            if (el) inputRefs.current[tempKey] = el;
                            else delete inputRefs.current[tempKey];
                          }}
                          type="number"
                          value={tempEntryData[tempKey] ?? ""}
                          onKeyDown={(e) => handleKeyDown(e, cat.id, date)}
                          onChange={(e) => handleInputChange(cat.id, date, e.target.value)}
                          style={{ width: "70px" }}
                        />
                      ) : savedEntry ? (
                        <>
                          <span>${savedEntry.amount}</span>
                          <button
                            onClick={() => {
                              setTempEntryData((prev) => ({
                                ...prev,
                                [tempKey]: savedEntry.amount,
                              }));
                            }}
                            style={{ marginLeft: "4px", background: "none", border: "none", color: "blue" }}
                          >
                            ✏️
                          </button>
                        </>
                      ) : (
                        <input
                          ref={(el) => {
                            if (el) inputRefs.current[tempKey] = el;
                            else delete inputRefs.current[tempKey];
                          }}
                          type="number"
                          value={tempEntryData[tempKey] ?? ""}
                          onKeyDown={(e) => handleKeyDown(e, cat.id, date)}
                          onChange={(e) => handleInputChange(cat.id, date, e.target.value)}
                          style={{ width: "80px" }}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}

            <tr>
              <td><strong>Required Cash</strong></td>
              {daysToShow.map((date) => (
                <td key={`required_cash_${date}`}>
                  <strong>${Number(subwayValuesByDate[date]?.requiredCash || 0).toFixed(2)}</strong>
                </td>
              ))}
            </tr>

            <tr>
              <td></td>
              {daysToShow.map((date) => (
                <td key={`submit_${date}`}>
                  <button className="cash-in-save" onClick={() => handleSaveForDate(date)}>
                    Save
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {showAddCat && (
        <div className="cash-in-cat-pop">
          <div>
            <h3>Add Category</h3>
            <form onSubmit={handleAddCategory}>
              <div>
                <label>
                  Name{" "}
                  <input
                    type="text"
                    value={newCat.name}
                    onChange={(e) => setNewCat((s) => ({ ...s, name: e.target.value }))}
                  />
                </label>
              </div>
              <div>
                <label>
                  Type{" "}
                  <select
                    value={newCat.category_type}
                    onChange={(e) =>
                      setNewCat((s) => ({ ...s, category_type: e.target.value }))
                    }
                  >
                    <option value="">-- Select --</option>
                    {CATEGORY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button type="button" onClick={() => { resetAddCat(); setShowAddCat(false); }}>
                  Cancel
                </button>
                <button type="submit">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditCat && (
        <div className="cash-in-edit-pop">
          <div>
            <h3>Edit / Disable Category</h3>
            <form onSubmit={handleSaveEditedCategory}>
              <div>
                <label>
                  Select{" "}
                  <select value={editCat.id} onChange={(e) => handleEditCatSelect(e.target.value)}>
                    <option value="">-- Choose --</option>
                    {editableCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.category_type})
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div>
                <label>
                  Name{" "}
                  <input
                    type="text"
                    value={editCat.name}
                    onChange={(e) => setEditCat((s) => ({ ...s, name: e.target.value }))}
                  />
                </label>
              </div>

              <div>
                <label>
                  Type{" "}
                  <select
                    value={editCat.category_type}
                    onChange={(e) =>
                      setEditCat((s) => ({ ...s, category_type: e.target.value }))
                    }
                  >
                    <option value="">-- Select --</option>
                    {CATEGORY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "space-between" }}>
                <button type="button" onClick={handleDisableCategory} style={{ color: "red" }}>
                  Disable
                </button>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={() => { resetEditCat(); setShowEditCat(false); }}>
                    Cancel
                  </button>
                  <button type="submit">Save</button>
                </div>
              </div>
            </form>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Note: “Open Cash”, “Close Cash”, “Required Cash”, “Cash Advance”, and any “till” type cannot be edited or disabled.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubwayCashInPage;




// import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";
// import { format, addDays, subDays, parseISO } from "date-fns";
// import "./CashEntriesPage.css";
// import { MdClose } from "react-icons/md";
// import { TbArrowBigLeftFilled, TbArrowBigRightFilled } from "react-icons/tb";
// import Papa from "papaparse";

// const SubwayCashInPage = () => {
//   const { id: businessId } = useParams();

//   const [storeNumber, setStoreNumber] = useState("");
//   const [categories, setCategories] = useState([]);
//   const [employees, setEmployees] = useState([]);
//   const [entryData, setEntryData] = useState({});
//   const [cashAdvanceData, setCashAdvanceData] = useState({});
//   const [tempEntryData, setTempEntryData] = useState({});
//   const [tempCashAdvanceData, setTempCashAdvanceData] = useState({});
//   const [salesRows, setSalesRows] = useState([]);
//   const [tenderRows, setTenderRows] = useState([]);

//   const [currentDate, setCurrentDate] = useState(new Date());
//   const [showDatePicker, setShowDatePicker] = useState(false);
//   const [startDateInput, setStartDateInput] = useState(format(new Date(), "yyyy-MM-dd"));

//   const [showAddCat, setShowAddCat] = useState(false);
//   const [showEditCat, setShowEditCat] = useState(false);
//   const [newCat, setNewCat] = useState({ name: "", category_type: "" });
//   const [editCat, setEditCat] = useState({ id: "", name: "", category_type: "" });

//   const [loadingStore, setLoadingStore] = useState(true);
//   const [syncing, setSyncing] = useState(false);

//   const inputRefs = useRef({});
//   const fileInputRefCash = useRef(null);

//   const CATEGORY_TYPES = [
//     { value: "income", label: "income" },
//     { value: "non-sale", label: "non-sale income" },
//     { value: "payment", label: "payment" },
//     { value: "paidouts", label: "paidouts" },
//     { value: "refunds", label: "refunds" },
//   ];

//   const RESERVED = new Set(["Open Cash", "Close Cash", "Required Cash", "Cash Advance"]);

//   const daysToShow = [-3, -2, -1, 0, 1, 2, 3].map((offset) =>
//     format(addDays(currentDate, offset), "yyyy-MM-dd")
//   );

//   const norm = (s = "") => s.trim().toLowerCase();

//   const loadBusinessStore = useCallback(async () => {
//     try {
//       setLoadingStore(true);
//       const { data } = await api.get(`/subway-data/business-store/${businessId}`);
//       setStoreNumber(String(data?.store_number || "").trim());
//     } catch (err) {
//       console.error("Error loading business store number:", err);
//       alert(err?.response?.data?.message || "Failed to load business store number");
//       setStoreNumber("");
//     } finally {
//       setLoadingStore(false);
//     }
//   }, [businessId]);

//   const loadCategories = useCallback(async () => {
//     try {
//       const { data } = await api.get(`/cash-register-categories`, {
//         params: { business_id: businessId },
//       });
//       setCategories(data);
//     } catch (err) {
//       console.error("Error fetching categories:", err);
//       alert("Failed to load categories");
//     }
//   }, [businessId]);

//   const loadEmployees = useCallback(async () => {
//     try {
//       const { data } = await api.get(`/load-employees`, {
//         params: { business_id: businessId },
//       });
//       setEmployees(data);
//     } catch (err) {
//       console.error("Error fetching employees:", err);
//       alert("Failed to load employees");
//     }
//   }, [businessId]);

//   const loadEntries = useCallback(async () => {
//     try {
//       const startDate = format(addDays(currentDate, -3), "yyyy-MM-dd");
//       const endDate = format(addDays(currentDate, 3), "yyyy-MM-dd");

//       const { data } = await api.get("/cash-register-entries", {
//         params: { business_id: businessId, start_date: startDate, end_date: endDate },
//       });

//       const newEntryData = {};
//       const newCashAdvanceData = {};

//       data.forEach((entry) => {
//         const dateKey = format(new Date(entry.entry_date), "yyyy-MM-dd");
//         const key = `${entry.category_id}_${dateKey}`;

//         if (entry.category_name === "Cash Advance") {
//           if (!newCashAdvanceData[dateKey]) newCashAdvanceData[dateKey] = [];
//           newCashAdvanceData[dateKey].push({
//             id: entry.id,
//             employee_id: entry.employee_id,
//             amount: entry.amount,
//             employee_name: entry.employee_name,
//           });
//         } else {
//           newEntryData[key] = { id: entry.id, amount: entry.amount };
//         }
//       });

//       setEntryData(newEntryData);
//       setCashAdvanceData(newCashAdvanceData);
//     } catch (err) {
//       console.error("Error loading entries:", err);
//       alert("Failed to load saved entries");
//     }
//   }, [businessId, currentDate]);

//   const loadSalesRows = useCallback(async () => {
//     if (!storeNumber) {
//       setSalesRows([]);
//       return;
//     }

//     try {
//       const { data } = await api.get(`/subway-data/sales/${storeNumber}`);
//       setSalesRows(Array.isArray(data) ? data : []);
//     } catch (err) {
//       console.error("Error loading subway sales:", err);
//       alert("Failed to load subway sales");
//       setSalesRows([]);
//     }
//   }, [storeNumber]);

//   const loadTenderRows = useCallback(async () => {
//     if (!storeNumber) {
//       setTenderRows([]);
//       return;
//     }

//     try {
//       const { data } = await api.get(`/subway-data/tenders/${storeNumber}`);
//       setTenderRows(Array.isArray(data) ? data : []);
//     } catch (err) {
//       console.error("Error loading subway tenders:", err);
//       alert("Failed to load subway tenders");
//       setTenderRows([]);
//     }
//   }, [storeNumber]);

//   useEffect(() => {
//     setEntryData({});
//     setCashAdvanceData({});
//     setTempEntryData({});
//     setTempCashAdvanceData({});
//   }, [businessId]);

//   useEffect(() => {
//     loadBusinessStore();
//     loadCategories();
//     loadEmployees();
//   }, [loadBusinessStore, loadCategories, loadEmployees]);

//   useEffect(() => {
//     if (!categories.length) return;
//     loadEntries();
//   }, [categories, loadEntries]);

//   useEffect(() => {
//     if (!storeNumber) return;
//     loadSalesRows();
//     loadTenderRows();
//   }, [storeNumber, loadSalesRows, loadTenderRows]);

//   const salesByDate = useMemo(() => {
//     const map = {};
//     salesRows.forEach((row) => {
//       const dateKey = String(row.sales_date || "").slice(0, 10);
//       if (!dateKey) return;
//       map[dateKey] = {
//         netSales: Number(row.net_sales || 0),
//         salesTax: Number(row.sales_tax || 0),
//       };
//     });
//     return map;
//   }, [salesRows]);

//   const tenderTotalsByDate = useMemo(() => {
//     const map = {};

//     tenderRows.forEach((row) => {
//       const dateKey = String(row.transaction_date || "").slice(0, 10);
//       if (!dateKey) return;

//       if (!map[dateKey]) {
//         map[dateKey] = {
//           cashTender: 0,
//           totalChange: 0,
//           byType: {},
//         };
//       }

//       const tenderType = String(row.tender_type || "").trim();
//       const amount = Number(row.amount || 0);
//       const changeAmount = Number(row.change_amount || 0);

//       if (!map[dateKey].byType[tenderType]) {
//         map[dateKey].byType[tenderType] = 0;
//       }

//       map[dateKey].byType[tenderType] += amount;

//       if (tenderType.toLowerCase().includes("cash")) {
//         map[dateKey].cashTender += amount;
//         map[dateKey].totalChange += changeAmount;
//       }
//     });

//     return map;
//   }, [tenderRows]);

//   const rowIds = useMemo(() => {
//     const paidoutRows = categories
//       .filter(
//         (c) =>
//           c.category_type === "paidouts" &&
//           c.name !== "Cash Advance" &&
//           c.name !== "Required Cash"
//       )
//       .map((c) => c.id);

//     return paidoutRows;
//   }, [categories]);

//   const handleKeyDown = useCallback(
//     (e, catId, date) => {
//       const key = e.key;
//       const goDown = key === "Enter" || (key === "Tab" && !e.shiftKey);
//       const goUp = key === "Tab" && e.shiftKey;
//       if (!goDown && !goUp) return;

//       e.preventDefault();

//       if (!rowIds.length) return;
//       const curIndex = rowIds.indexOf(catId);
//       if (curIndex === -1) return;

//       const isCellEmpty = (nextCatId, nextDate) => {
//         const k = `${nextCatId}_${nextDate}`;
//         return !entryData[k] && tempEntryData[k] === undefined && !!inputRefs.current[k];
//       };

//       let idx = curIndex;
//       let steps = 0;

//       while (steps < rowIds.length) {
//         idx = goDown ? (idx + 1) % rowIds.length : (idx - 1 + rowIds.length) % rowIds.length;
//         const nextCatId = rowIds[idx];
//         if (isCellEmpty(nextCatId, date)) {
//           const nextEl = inputRefs.current[`${nextCatId}_${date}`];
//           if (nextEl) {
//             nextEl.focus();
//             if (typeof nextEl.select === "function") nextEl.select();
//           }
//           return;
//         }
//         steps += 1;
//       }
//     },
//     [rowIds, entryData, tempEntryData]
//   );

//   const handleInputChange = (categoryId, date, value) => {
//     const key = `${categoryId}_${date}`;
//     setTempEntryData((prev) => ({ ...prev, [key]: value }));
//   };

//   const handleCashAdvanceChange = (date, index, field, value) => {
//     setTempCashAdvanceData((prev) => {
//       const updated = { ...prev };
//       if (!updated[date]) updated[date] = [];
//       if (!updated[date][index]) updated[date][index] = {};
//       updated[date][index][field] = value;
//       return updated;
//     });
//   };

//   const addCashAdvanceRow = (date) => {
//     setTempCashAdvanceData((prev) => {
//       const updated = { ...prev };
//       if (!updated[date]) updated[date] = [];
//       updated[date].push({ employee_id: "", amount: "" });
//       return updated;
//     });
//   };

//   const removeCashAdvanceRow = (date, index) => {
//     setTempCashAdvanceData((prev) => {
//       const updated = { ...prev };
//       if (!updated[date]) return updated;
//       updated[date] = updated[date].filter((_, i) => i !== index);
//       return updated;
//     });
//   };

//   const nameExists = (name, exceptId = null) =>
//     categories.some(
//       (c) => c.name.toLowerCase() === name.trim().toLowerCase() && c.id !== exceptId
//     );

//   const cannotTouch = (cat) => !cat || cat.category_type === "till" || RESERVED.has(cat.name);

//   const editableCategories = categories.filter((c) => !cannotTouch(c));

//   const resetAddCat = () => setNewCat({ name: "", category_type: "" });
//   const resetEditCat = () => setEditCat({ id: "", name: "", category_type: "" });

//   const handleAddCategory = async (e) => {
//     e.preventDefault();
//     if (!newCat.name.trim() || !newCat.category_type) {
//       alert("Enter name and select type.");
//       return;
//     }
//     if (RESERVED.has(newCat.name.trim())) {
//       alert("That name is reserved.");
//       return;
//     }
//     if (nameExists(newCat.name)) {
//       alert("Category name already exists.");
//       return;
//     }
//     try {
//       await api.post("/cash-register-categories", {
//         name: newCat.name.trim(),
//         category_type: newCat.category_type,
//         business_id: businessId,
//       });
//       await loadCategories();
//       resetAddCat();
//       setShowAddCat(false);
//     } catch (err) {
//       console.error("Add category error:", err);
//       alert("Failed to add category");
//     }
//   };

//   const handleEditCatSelect = (idStr) => {
//     const id = parseInt(idStr, 10);
//     const cat = categories.find((c) => c.id === id);
//     if (!cat) return;
//     if (cannotTouch(cat)) {
//       alert("This category can’t be edited.");
//       return;
//     }
//     setEditCat({ id: cat.id, name: cat.name, category_type: cat.category_type });
//   };

//   const handleSaveEditedCategory = async (e) => {
//     e.preventDefault();
//     if (!editCat.id || !editCat.name.trim() || !editCat.category_type) {
//       alert("Select a category and fill all fields.");
//       return;
//     }
//     const original = categories.find((c) => c.id === editCat.id);
//     if (cannotTouch(original)) {
//       alert("This category can’t be edited.");
//       return;
//     }
//     if (nameExists(editCat.name, editCat.id)) {
//       alert("Another category with this name already exists.");
//       return;
//     }
//     try {
//       await api.put(`/cash-register-categories/${editCat.id}`, {
//         name: editCat.name.trim(),
//         category_type: editCat.category_type,
//       });
//       await loadCategories();
//       resetEditCat();
//       setShowEditCat(false);
//     } catch (err) {
//       console.error("Update category error:", err);
//       alert("Failed to update category");
//     }
//   };

//   const handleDisableCategory = async () => {
//     if (!editCat.id) {
//       alert("Select a category first.");
//       return;
//     }
//     const cat = categories.find((c) => c.id === editCat.id);
//     if (cannotTouch(cat)) {
//       alert("This category can’t be disabled.");
//       return;
//     }
//     if (!window.confirm(`Disable "${cat.name}"?`)) return;

//     try {
//       await api.put(`/cash-register-categories/${editCat.id}/disable`);
//       await loadCategories();
//       resetEditCat();
//       setShowEditCat(false);
//     } catch (err) {
//       console.error("Disable category error:", err);
//       alert("Failed to disable category");
//     }
//   };

//   const handleJumpToDate = useCallback(() => {
//     if (!startDateInput) return;
//     const dt = parseISO(startDateInput);
//     if (isNaN(dt.getTime())) return;
//     setCurrentDate(dt);
//     setShowDatePicker(false);
//   }, [startDateInput]);

//   const handleCashCsvUpload = (e) => {
//     const file = e.target.files?.[0];
//     if (!file) return;

//     const byNorm = {};
//     categories.forEach((c) => {
//       byNorm[norm(c.name)] = c;
//     });

//     Papa.parse(file, {
//       header: true,
//       skipEmptyLines: true,
//       transformHeader: (h) => (h || "").trim(),
//       complete: async (res) => {
//         try {
//           const headers = res.meta?.fields || [];
//           const data = Array.isArray(res.data) ? res.data : [];

//           const dateHeader = headers.find((h) => norm(h) === "date");
//           if (!dateHeader) {
//             alert('Missing required "Date" column.');
//             if (fileInputRefCash.current) fileInputRefCash.current.value = "";
//             return;
//           }

//           const RESERVED_HEADERS = new Set([
//             "required cash",
//             "cash advance",
//             "net sales",
//             "sales tax",
//             "cash tender",
//             "total change",
//           ]);

//           const mappings = [];
//           const ignoredColumns = [];

//           for (const h of headers) {
//             const n = norm(h);
//             if (!h || n === "date") continue;
//             if (RESERVED_HEADERS.has(n)) continue;

//             const cat = byNorm[n];
//             if (!cat) {
//               ignoredColumns.push(h);
//               continue;
//             }

//             mappings.push({
//               column: h,
//               category_id: cat.id,
//               category_name: cat.name,
//             });
//           }

//           if (!mappings.length) {
//             alert("No importable paidout category columns found.");
//             if (fileInputRefCash.current) fileInputRefCash.current.value = "";
//             return;
//           }

//           const parseAmount = (v) => {
//             const s = String(v == null ? "" : v).trim().replace(/[$,]/g, "");
//             if (!s) return null;
//             const n = parseFloat(s);
//             return isNaN(n) ? null : Number(n.toFixed(2));
//           };

//           const entries = [];
//           const seen = new Set();
//           let duplicateFound = false;

//           data.forEach((row, idx) => {
//             if (duplicateFound) return;

//             const rowNum = idx + 2;
//             const date = String(row[dateHeader] || "").trim();
//             if (!date) return;

//             for (const m of mappings) {
//               const amt = parseAmount(row[m.column]);
//               if (amt === null) continue;

//               const key = `${date}|${m.category_id}`;
//               if (seen.has(key)) {
//                 alert(
//                   `Duplicate value for Date ${date} and Category "${m.category_name}" (CSV row ${rowNum}).`
//                 );
//                 duplicateFound = true;
//                 return;
//               }

//               seen.add(key);

//               entries.push({
//                 entry_date: date,
//                 category_id: m.category_id,
//                 amount: amt,
//               });
//             }
//           });

//           if (duplicateFound) {
//             if (fileInputRefCash.current) fileInputRefCash.current.value = "";
//             return;
//           }

//           if (!entries.length) {
//             alert("No numeric amounts found to import.");
//             if (fileInputRefCash.current) fileInputRefCash.current.value = "";
//             return;
//           }

//           await api.post("/cash-register-entries/import-json", {
//             business_id: Number(businessId),
//             entries,
//           });

//           let successMessage = `Imported ${entries.length} entries.`;
//           if (ignoredColumns.length) {
//             successMessage += ` Ignored columns: ${ignoredColumns.join(", ")}`;
//           }

//           alert(successMessage);

//           setTempEntryData({});
//           setTempCashAdvanceData({});
//           await loadEntries();
//         } catch (err) {
//           console.error("Cash import failed:", err);
//           const msg =
//             err?.response?.data?.errors?.join("\n") ||
//             err?.response?.data?.error ||
//             "Import failed.";
//           alert(msg);
//         } finally {
//           if (fileInputRefCash.current) fileInputRefCash.current.value = "";
//         }
//       },
//       error: (err) => {
//         console.error("CSV parse error:", err);
//         alert("Failed to parse CSV.");
//         if (fileInputRefCash.current) fileInputRefCash.current.value = "";
//       },
//     });
//   };

//   const handleSaveForDate = async (date) => {
//     const entriesToUpdate = [];
//     const entriesToInsert = [];

//     Object.entries(tempEntryData).forEach(([key, value]) => {
//       const [category_id, entryDate] = key.split("_");
//       if (entryDate !== date) return;
//       if (value === "") return;

//       const parsedAmount = parseFloat(value);
//       if (isNaN(parsedAmount)) return;

//       const savedEntry = entryData[key];
//       if (savedEntry?.id) {
//         entriesToUpdate.push({
//           id: savedEntry.id,
//           amount: parsedAmount,
//           notes: null,
//         });
//       } else {
//         entriesToInsert.push({
//           category_id: parseInt(category_id, 10),
//           date,
//           amount: parsedAmount,
//           notes: null,
//         });
//       }
//     });

//     const cashAdvanceCat = categories.find((c) => c.name === "Cash Advance");

//     (tempCashAdvanceData[date] || []).forEach((row) => {
//       if (!row.employee_id || !row.amount) return;
//       const parsedAmount = parseFloat(row.amount);
//       if (isNaN(parsedAmount)) return;

//       if (row.id) {
//         entriesToUpdate.push({
//           id: row.id,
//           employee_id: parseInt(row.employee_id, 10),
//           amount: parsedAmount,
//           notes: null,
//         });
//       } else {
//         entriesToInsert.push({
//           category_id: cashAdvanceCat?.id,
//           date,
//           employee_id: parseInt(row.employee_id, 10),
//           amount: parsedAmount,
//           notes: null,
//         });
//       }
//     });

//     if (!entriesToUpdate.length && !entriesToInsert.length) {
//       alert(`No entries to save for ${date}`);
//       return;
//     }

//     try {
//       if (entriesToInsert.length) {
//         await api.post("/cash-register-entries", {
//           business_id: businessId,
//           entries: entriesToInsert,
//         });
//       }

//       if (entriesToUpdate.length) {
//         await api.put("/cash-register-entries", {
//           business_id: businessId,
//           entries: entriesToUpdate,
//         });
//       }

//       setTempEntryData((prev) => {
//         const updated = { ...prev };
//         Object.keys(updated).forEach((key) => {
//           const [, entryDate] = key.split("_");
//           if (entryDate === date) delete updated[key];
//         });
//         return updated;
//       });

//       setTempCashAdvanceData((prev) => {
//         const updated = { ...prev };
//         delete updated[date];
//         return updated;
//       });

//       await loadEntries();
//     } catch (err) {
//       console.error("Save error:", err);
//       alert("Failed to save entries for this date");
//     }
//   };

//   const paidoutCategories = useMemo(() => {
//     return categories.filter(
//       (c) =>
//         c.category_type === "paidouts" &&
//         c.name !== "Cash Advance" &&
//         c.name !== "Required Cash"
//     );
//   }, [categories]);

//   const cashAdvanceCat = categories.find((c) => c.name === "Cash Advance");

//   const subwayValuesByDate = useMemo(() => {
//     const values = {};

//     daysToShow.forEach((date) => {
//       const sales = salesByDate[date] || { netSales: 0, salesTax: 0 };
//       const tenders = tenderTotalsByDate[date] || { cashTender: 0, totalChange: 0 };

//       const paidoutsTotal = paidoutCategories.reduce((sum, cat) => {
//         const key = `${cat.id}_${date}`;
//         const tempVal = tempEntryData[key];
//         const savedVal = entryData[key]?.amount;
//         const amount =
//           tempVal !== undefined && tempVal !== ""
//             ? parseFloat(tempVal)
//             : savedVal !== undefined
//             ? parseFloat(savedVal)
//             : 0;
//         return sum + (isNaN(amount) ? 0 : amount);
//       }, 0);

//       const cashAdvanceTotal = [
//         ...(cashAdvanceData[date] || []),
//         ...(tempCashAdvanceData[date] || []),
//       ].reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);

//       values[date] = {
//         netSales: sales.netSales,
//         salesTax: sales.salesTax,
//         cashTender: tenders.cashTender,
//         totalChange: tenders.totalChange,
//         paidoutsTotal,
//         cashAdvanceTotal,
//         requiredCash:
//           tenders.cashTender - tenders.totalChange - paidoutsTotal - cashAdvanceTotal,
//       };
//     });

//     return values;
//   }, [
//     daysToShow,
//     salesByDate,
//     tenderTotalsByDate,
//     paidoutCategories,
//     entryData,
//     tempEntryData,
//     cashAdvanceData,
//     tempCashAdvanceData,
//   ]);

//   const goLeft = () => setCurrentDate((prev) => subDays(prev, 1));
//   const goRight = () => setCurrentDate((prev) => addDays(prev, 1));

//   if (loadingStore) {
//     return <div className="cash-entries-page"><h1 className="pw-page-title">Loading Subway Cash In...</h1></div>;
//   }

//   return (
//     <div className="cash-entries-page">
//       <h1 className="pw-page-title">Subway Cash In</h1>

//       <div className="cash-in-cat">
//         <button onClick={() => setShowAddCat(true)}>+ Add Category</button>
//         <button onClick={() => setShowEditCat(true)}>✏️ Edit / Disable Categories</button>
//         <button onClick={() => fileInputRefCash.current?.click()}>Import Cash CSV</button>
//         <input
//           type="file"
//           accept=".csv"
//           ref={fileInputRefCash}
//           style={{ display: "none" }}
//           onChange={handleCashCsvUpload}
//         />
//         <button
//           onClick={() => {
//             setStartDateInput(format(currentDate, "yyyy-MM-dd"));
//             setShowDatePicker((s) => !s);
//           }}
//           className="cash-jump"
//           aria-label="Jump to date"
//         >
//           🗓️ Jump to date
//         </button>

//         <button
//           onClick={async () => {
//             try {
//               setSyncing(true);
//               await api.post("/subway-data/sync", {
//                 start_date: format(addDays(currentDate, -3), "yyyy-MM-dd"),
//                 end_date: format(addDays(currentDate, 3), "yyyy-MM-dd"),
//               });
//               await loadSalesRows();
//               await loadTenderRows();
//               alert("Subway data synced.");
//             } catch (err) {
//               console.error("Subway sync failed:", err);
//               alert(err?.response?.data?.message || "Failed to sync Subway data");
//             } finally {
//               setSyncing(false);
//             }
//           }}
//           disabled={syncing}
//         >
//           {syncing ? "Syncing..." : "Sync Subway"}
//         </button>

//         <div className="cash-nav-center">
//           {showDatePicker && (
//             <span className="cash-jump-controls">
//               <input
//                 type="date"
//                 value={startDateInput}
//                 onChange={(e) => setStartDateInput(e.target.value)}
//               />
//               <button onClick={handleJumpToDate}>Go</button>
//               <button
//                 onClick={() => setShowDatePicker(false)}
//                 style={{ background: "none", color: "red" }}
//               >
//                 <MdClose />
//               </button>
//             </span>
//           )}
//         </div>
//       </div>

//       <div className="cash-nav">
//         <button className="left-nav-arrow" aria-label="Previous day" onClick={goLeft}>
//           <TbArrowBigLeftFilled />
//         </button>
//         <button className="right-nav-arrow" aria-label="Next day" onClick={goRight}>
//           <TbArrowBigRightFilled />
//         </button>
//       </div>

//       <div className="entries-grid" style={{ overflowX: "auto" }}>
//         <table
//           className="cash-entry-table"
//           border="1"
//           cellPadding="8"
//           style={{ borderCollapse: "collapse", minWidth: "100%" }}
//         >
//           <thead>
//             <tr>
//               <th>Category</th>
//               {daysToShow.map((date) => (
//                 <th key={date}>{format(parseISO(date), "MMM d")}</th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             <tr>
//               <td><strong>Store Number</strong></td>
//               {daysToShow.map((date) => (
//                 <td key={`store_${date}`}>{storeNumber}</td>
//               ))}
//             </tr>

//             <tr>
//               <td><strong>Net Sales</strong></td>
//               {daysToShow.map((date) => (
//                 <td key={`net_sales_${date}`}>${subwayValuesByDate[date]?.netSales.toFixed(2)}</td>
//               ))}
//             </tr>

//             <tr>
//               <td><strong>Sales Tax</strong></td>
//               {daysToShow.map((date) => (
//                 <td key={`sales_tax_${date}`}>${subwayValuesByDate[date]?.salesTax.toFixed(2)}</td>
//               ))}
//             </tr>

//             <tr>
//               <td><strong>Cash Tender</strong></td>
//               {daysToShow.map((date) => (
//                 <td key={`cash_tender_${date}`}>${subwayValuesByDate[date]?.cashTender.toFixed(2)}</td>
//               ))}
//             </tr>

//             <tr>
//               <td><strong>Total Change</strong></td>
//               {daysToShow.map((date) => (
//                 <td key={`total_change_${date}`}>${subwayValuesByDate[date]?.totalChange.toFixed(2)}</td>
//               ))}
//             </tr>

//             {paidoutCategories.map((cat) => (
//               <tr key={cat.id}>
//                 <td>{cat.name}</td>
//                 {daysToShow.map((date) => {
//                   const savedEntry = entryData[`${cat.id}_${date}`];
//                   const tempKey = `${cat.id}_${date}`;

//                   return (
//                     <td key={`${cat.id}_${date}`}>
//                       {tempEntryData[tempKey] !== undefined ? (
//                         <input
//                           ref={(el) => {
//                             if (el) inputRefs.current[tempKey] = el;
//                             else delete inputRefs.current[tempKey];
//                           }}
//                           type="number"
//                           value={tempEntryData[tempKey] ?? ""}
//                           onKeyDown={(e) => handleKeyDown(e, cat.id, date)}
//                           onChange={(e) => handleInputChange(cat.id, date, e.target.value)}
//                           style={{ width: "70px" }}
//                         />
//                       ) : savedEntry ? (
//                         <>
//                           <span>${savedEntry.amount}</span>
//                           <button
//                             onClick={() => {
//                               setTempEntryData((prev) => ({
//                                 ...prev,
//                                 [tempKey]: savedEntry.amount,
//                               }));
//                             }}
//                             style={{ marginLeft: "4px", background: "none", border: "none", color: "blue" }}
//                           >
//                             ✏️
//                           </button>
//                         </>
//                       ) : (
//                         <input
//                           ref={(el) => {
//                             if (el) inputRefs.current[tempKey] = el;
//                             else delete inputRefs.current[tempKey];
//                           }}
//                           type="number"
//                           value={tempEntryData[tempKey] ?? ""}
//                           onKeyDown={(e) => handleKeyDown(e, cat.id, date)}
//                           onChange={(e) => handleInputChange(cat.id, date, e.target.value)}
//                           style={{ width: "80px" }}
//                         />
//                       )}
//                     </td>
//                   );
//                 })}
//               </tr>
//             ))}

//             {cashAdvanceCat && (
//               <tr>
//                 <td><strong>Cash Advance</strong></td>
//                 {daysToShow.map((date) => {
//                   const displayCashAdvanceRows =
//                     tempCashAdvanceData[date] ?? cashAdvanceData[date] ?? [];

//                   return (
//                     <td key={`ca_${date}`}>
//                       {displayCashAdvanceRows.map((entry, idx) => (
//                         <div
//                           key={idx}
//                           className="cash-in-advance"
//                           style={{ marginBottom: "6px", display: "flex", gap: "4px", alignItems: "center" }}
//                         >
//                           {entry.employee_name ? (
//                             <>
//                               <span>{entry.employee_name}: ${entry.amount}</span>
//                               <button
//                                 onClick={() => {
//                                   setTempCashAdvanceData((prev) => {
//                                     const updated = { ...prev };
//                                     if (!updated[date]) updated[date] = [];
//                                     updated[date].push({
//                                       id: entry.id,
//                                       employee_id: entry.employee_id,
//                                       amount: entry.amount,
//                                     });
//                                     return updated;
//                                   });
//                                   setCashAdvanceData((prev) => {
//                                     const updated = { ...prev };
//                                     updated[date] = updated[date].filter((_, i) => i !== idx);
//                                     return updated;
//                                   });
//                                 }}
//                                 style={{ background: "none", border: "none", color: "blue", marginLeft: "4px" }}
//                               >
//                                 ✏️
//                               </button>
//                             </>
//                           ) : (
//                             <>
//                               <select
//                                 value={entry.employee_id || ""}
//                                 onChange={(e) =>
//                                   handleCashAdvanceChange(date, idx, "employee_id", e.target.value)
//                                 }
//                                 style={{ flex: 1 }}
//                               >
//                                 <option value="">Select Employee</option>
//                                 {employees.map((emp) => (
//                                   <option key={emp.id} value={emp.id}>
//                                     {emp.name}
//                                   </option>
//                                 ))}
//                               </select>
//                               <input
//                                 type="number"
//                                 placeholder="Amount"
//                                 value={entry.amount || ""}
//                                 onChange={(e) =>
//                                   handleCashAdvanceChange(date, idx, "amount", e.target.value)
//                                 }
//                                 style={{ width: "80px" }}
//                               />
//                               <button onClick={() => removeCashAdvanceRow(date, idx)} className="remove-advance">
//                                 −
//                               </button>
//                             </>
//                           )}
//                         </div>
//                       ))}
//                       <button className="add-advance" onClick={() => addCashAdvanceRow(date)}>
//                         + Add
//                       </button>
//                     </td>
//                   );
//                 })}
//               </tr>
//             )}

//             <tr>
//               <td><strong>Required Cash</strong></td>
//               {daysToShow.map((date) => (
//                 <td key={`required_cash_${date}`}>
//                   <strong>${Number(subwayValuesByDate[date]?.requiredCash || 0).toFixed(2)}</strong>
//                 </td>
//               ))}
//             </tr>

//             <tr>
//               <td></td>
//               {daysToShow.map((date) => (
//                 <td key={`submit_${date}`}>
//                   <button className="cash-in-save" onClick={() => handleSaveForDate(date)}>
//                     Save
//                   </button>
//                 </td>
//               ))}
//             </tr>
//           </tbody>
//         </table>
//       </div>

//       {showAddCat && (
//         <div className="cash-in-cat-pop">
//           <div>
//             <h3>Add Category</h3>
//             <form onSubmit={handleAddCategory}>
//               <div>
//                 <label>
//                   Name{" "}
//                   <input
//                     type="text"
//                     value={newCat.name}
//                     onChange={(e) => setNewCat((s) => ({ ...s, name: e.target.value }))}
//                   />
//                 </label>
//               </div>
//               <div>
//                 <label>
//                   Type{" "}
//                   <select
//                     value={newCat.category_type}
//                     onChange={(e) =>
//                       setNewCat((s) => ({ ...s, category_type: e.target.value }))
//                     }
//                   >
//                     <option value="">-- Select --</option>
//                     {CATEGORY_TYPES.map((t) => (
//                       <option key={t.value} value={t.value}>{t.label}</option>
//                     ))}
//                   </select>
//                 </label>
//               </div>
//               <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
//                 <button type="button" onClick={() => { resetAddCat(); setShowAddCat(false); }}>
//                   Cancel
//                 </button>
//                 <button type="submit">Add</button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {showEditCat && (
//         <div className="cash-in-edit-pop">
//           <div>
//             <h3>Edit / Disable Category</h3>
//             <form onSubmit={handleSaveEditedCategory}>
//               <div>
//                 <label>
//                   Select{" "}
//                   <select value={editCat.id} onChange={(e) => handleEditCatSelect(e.target.value)}>
//                     <option value="">-- Choose --</option>
//                     {editableCategories.map((c) => (
//                       <option key={c.id} value={c.id}>
//                         {c.name} ({c.category_type})
//                       </option>
//                     ))}
//                   </select>
//                 </label>
//               </div>

//               <div>
//                 <label>
//                   Name{" "}
//                   <input
//                     type="text"
//                     value={editCat.name}
//                     onChange={(e) => setEditCat((s) => ({ ...s, name: e.target.value }))}
//                   />
//                 </label>
//               </div>

//               <div>
//                 <label>
//                   Type{" "}
//                   <select
//                     value={editCat.category_type}
//                     onChange={(e) =>
//                       setEditCat((s) => ({ ...s, category_type: e.target.value }))
//                     }
//                   >
//                     <option value="">-- Select --</option>
//                     {CATEGORY_TYPES.map((t) => (
//                       <option key={t.value} value={t.value}>{t.label}</option>
//                     ))}
//                   </select>
//                 </label>
//               </div>

//               <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "space-between" }}>
//                 <button type="button" onClick={handleDisableCategory} style={{ color: "red" }}>
//                   Disable
//                 </button>
//                 <div style={{ display: "flex", gap: 8 }}>
//                   <button type="button" onClick={() => { resetEditCat(); setShowEditCat(false); }}>
//                     Cancel
//                   </button>
//                   <button type="submit">Save</button>
//                 </div>
//               </div>
//             </form>
//             <div style={{ fontSize: 12, marginTop: 6 }}>
//               Note: “Open Cash”, “Close Cash”, “Required Cash”, “Cash Advance”, and any “till” type cannot be edited or disabled.
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default SubwayCashInPage;